import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useHistory, useLocation } from "react-router-dom";
import { MENU_ROOT_STATE } from "@/types/NavigationState";
import { useDispatch, useSelector } from "react-redux";
import SntButton from "@/cleanup/widgets/forms/SntButton";

import {
  MenuLevelConst,
  ORG_ALL,
  SCOPE_PARAM,
  useOrg,
} from "@/contexts/OrgContext";
import * as actions from "@/redux/actions";
import SntOrgMenuSelectorContainer from "@containers/SntOrgSelectorContainer/SntOrgMenuSelectorContainer";

import NavList, { buildMenuItemHref } from "./NavList/NavList";
import SidebarMenuSearch from "./SidebarMenuSearch";
import { filterMenuRoutes, firstMenuItem, highlightMenuRoutes } from "./Menus/menuSearch";
import useHomeMenu from "./Menus/useHomeMenu";
import useOwnLevelMenu from "./Menus/useOwnLevelMenu";
import useCrossLevelMenu, { type LevelPair } from "./Menus/useCrossLevelMenu";
import useNavigationModel from "./Menus/useNavigationModel";

import HomeIcon from "@images/menu/home.svg";
import CogIcon from "@images/menu/cog.svg";
import SensolusLogoIcon from "@images/sensolus/sensolus_logo_white.svg";
import { MenuSourceConst, MenuTypeConst, type MenuRoute, type MenuSource } from "./Menus/menuTypes";
import { getHashFromLandingPage } from "@/containers/LandingPageHelper/LandingPageHelper";
import type { RootState } from "@/types/RootState";
import type { LoginInfo } from "@/types/LoginInfo";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import { hasPermission } from "@/hooks/usePermissions";
import { UserPermission } from "@/types/generated/UserPermission";
import { hasFeature } from "@/hooks/useFeatures";
import { FeatureKey } from "@/types/generated/FeatureKey";
import useUser from "@hooks/useUser";
import useErrorDialog from "@/cleanup/widgets/dialog/useErrorDialog";
import SntSupportUserIcon from "@icons/SntSupportUserIcon";
import HeaderIconButton from "../Header/HeaderIconButton";

/**
 * Extended menu data with type and loading state
 */
interface MenuData {
  type: string;
  /** Which hook built this menu — see MenuSourceConst. */
  menuSource?: MenuSource;
  loading: boolean;
  /** The full tree, hidden entries included — what path resolution reads. */
  routes: MenuRoute[];
  /** The same tree with hidden entries pruned — what NavList renders. */
  renderRoutes?: MenuRoute[];
  allPaths: Record<string, MenuRoute>;
  availableMenuPaths: Record<string, MenuRoute>;
  forceMenuPaths?: Record<string, MenuRoute>;
  /** Cross-level menu only — see useCrossLevelMenu. */
  orgScopedPaths?: Record<string, true>;
  /** Cross-level menu only — see useCrossLevelMenu. */
  levelIndex?: Record<string, LevelPair>;
  defaultPath: { pathname: string; search: string } | string;
  isAdminMenuAvailable?: boolean;
}

/**
 * Organisation selector change data
 */
interface OrgSelectorData {
  value: number | null;
  /** Set by the System/Organizations tabs — see SntOrgMenuSelectorContainer. */
  scope?: "own" | "cross";
}

const SidebarBody = () => {
  const orgContext = useOrg();
  const { urlOrgId, isAllOrgs, updateOrg, level, withOrg } = orgContext ?? {
    urlOrgId: null,
    isAllOrgs: false,
    updateOrg: (_newOrgId: number | null) => {},
    level: MenuLevelConst.CROSS,
    withOrg: (url: string) => url,
  };

  /**
   * The organisation THE LEVEL is on, which is not always the one the url names.
   *
   * At `?org=196&scope=all` the url names org 196 — for the detail page open on
   * it — while the sidebar is at All. `scope` wins, so the menu and the org pill
   * must both read `null` here; only the page's own data may read the 196.
   * Everything below that asks "which org is the user browsing" wants this, not
   * `urlOrgId`; the two places that genuinely want the raw parameter (the
   * drill-in rule and the auto-slice guard) say so at their call site.
   */
  const levelOrgId = isAllOrgs ? null : urlOrgId;
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const loginInfo = useSelector((state: RootState) => state.user) as LoginInfo;
  const { whiteLabel } = loginInfo;

  const homeMenu = useHomeMenu(loginInfo) as MenuData;
  const ownLevelMenu = useOwnLevelMenu(loginInfo, levelOrgId ?? undefined, level) as MenuData;
  const crossLevelMenu = useCrossLevelMenu(loginInfo, levelOrgId ?? undefined) as MenuData;

  // Whether this login has a Home menu at all (STIC-15365): NORMAL always,
  // PARTNER only with the legacy_partner_home_menu feature key, SYSTEM never.
  // Read from here everywhere below rather than re-testing organisationType.
  const { homeMenuReachable, isSystemLogin, isPartnerLogin } = useNavigationModel(loginInfo);

  // The menu a login lands in when it arrives at "/" or its configured landing
  // page is out of reach: Home for whoever has one, the cross level for SYSTEM,
  // and the own level for a partner without Home — rule 7's fallback below.
  // Each then opens on its defaultPath (see MENU_RESOLUTION.md).
  const landingMenu = homeMenuReachable
    ? homeMenu
    : isSystemLogin
      ? crossLevelMenu
      : ownLevelMenu;

  const { onImpersonateUser, IMPERSONATE_TYPE, ImpersonateReasonDialogRoot } = useUser();
  const { handleOpen: openErrorDialog, ErrorDialog } = useErrorDialog({});

  const [selectedMenu, setSelectedMenu] = useState<MenuData>(homeMenu);

  // Text search over the admin menu (own and cross level). The home menu is
  // short and collapsible, so it goes without.
  const [menuQuery, setMenuQuery] = useState("");
  const isAdminMenu = selectedMenu.type !== MenuTypeConst.MAIN;

  // Rule: the field empties on a level change (own <-> cross, or back to the
  // home menu). A reload empties it by construction — nothing is persisted.
  useEffect(() => {
    setMenuQuery("");
  }, [selectedMenu.menuSource, selectedMenu.type]);

  // The pruned tree: hidden entries are already gone, so NavList draws whatever
  // it is handed. `selectedMenu.routes` still holds the full tree for path
  // resolution — see useAvailibleMenu.
  const visibleRoutes = selectedMenu.renderRoutes ?? selectedMenu.routes;
  const searchedRoutes = useMemo(
    () =>
      isAdminMenu && menuQuery.trim() !== ""
        ? highlightMenuRoutes(filterMenuRoutes(visibleRoutes, menuQuery), menuQuery)
        : visibleRoutes,
    [visibleRoutes, menuQuery, isAdminMenu]
  );
  const noMenuMatches = isAdminMenu && menuQuery.trim() !== "" && searchedRoutes.length === 0;

  // The org the menu was last resolved for, used to tell an org-context switch apart
  // from a fresh page load. On a switch we prefer the org default page over a dead-end
  // no_access screen when the current page isn't available in the new org (STIC-15494).
  const prevOrgIdRef = useRef<number | null>(levelOrgId);

  const [lastHomeMenu, setLastHomeMenu] = useState<{ pathname: string; search: string } | null>(null);
  const [lastAdminMenu, setLastAdminMenu] = useState<{ pathname: string; search: string } | null>(null);

  // The page the user was last on at each level, so the org pill can return to
  // it when the page it is leaving has no counterpart on the other side
  // (STIC-16144). List paths only: a detail subpage belongs to the record's
  // organisation and must not travel across a level switch — the same reason
  // onOrganisationChanged resolves `currentParent` before translating.
  //
  // Session-scoped by construction: plain state, so a reload starts over.
  const [lastOwnPage, setLastOwnPage] = useState<string | null>(null);
  const [lastCrossPage, setLastCrossPage] = useState<string | null>(null);

  /**
   * What the own-level menu offers *while standing at own level*.
   *
   * Every entry in `useOwnLevelMenu` is gated on `atOwnLevel`, so once the user
   * is at cross level `ownLevelMenu.availableMenuPaths` reports next to nothing
   * and cannot answer "would this page be visible if I switched back?". The
   * answer is only computable at own level, so it is snapshotted there and read
   * when switching back.
   *
   * Empty until the user has been at own level once — then the switch falls
   * through to the own menu's default page, which is what it did before.
   */
  const ownLevelAvailableRef = useRef<Record<string, MenuRoute>>({});

  const [showFooter, setShowFooter] = useState<boolean>(false);
  const [headerSlots, setHeaderSlots] = useState<{
    org: HTMLElement | null;
    support: HTMLElement | null;
  }>({ org: null, support: null });
  const { isMobile } = useSelector((state: RootState) => state.all);
  // Both SYSTEM and PARTNER logins browse other organisations, so both get the
  // header org selector. Its first tab is their own level ("System"/"Partner").
  // A partner in its Home menu is not browsing anything, so the pill is hidden
  // there and shown again in Admin (STIC-15365 R3). Without the legacy Home
  // menu the partner is never in Home, so the pill simply stays.
  const hasOrgSelector =
    isSystemLogin ||
    (isPartnerLogin && (!homeMenuReachable || selectedMenu.type !== MenuTypeConst.MAIN));

  // STIC-16225: partners support-login into their customers too. The pill itself
  // is rendered with `hideImpersonate`, so this flag is the only thing that puts
  // the button in the header slot — gating it on SYSTEM alone hid it from every
  // partner, even though the permissions (ORG_SUPPORT_LOGIN_* are
  // .forSystem().forPartner()) and the endpoints (@PreAuthorize accepts SYSTEM
  // or PARTNER) have always allowed it. Same login set as `hasOrgSelector`
  // above: whoever can browse another organisation can support-login into it.
  const canImpersonate =
    (isSystemLogin || isPartnerLogin) &&
    levelOrgId !== null &&
    levelOrgId !== 0 &&
    (hasPermission(UserPermission.ORG_SUPPORT_LOGIN_VIEW_ONLY) ||
      hasPermission(UserPermission.ORG_SUPPORT_LOGIN_ADMIN)) &&
    hasFeature(FeatureKey.impersonate);

  // Guards support login into one's own organisation. For a SYSTEM login that is
  // the system org (which the backend rejects outright); for a PARTNER login it
  // is the partner org. Own level carries no `?org=` at all (levelOrgId null), so
  // this only bites when the pill explicitly names the login's own org.
  const isTargetSystem = levelOrgId === loginInfo.organisation?.id;

  // The org pill derives its active tab from `value` alone: the login's own org
  // id lights up the own tab ("System" / "Partner"), 0 lights up "All", and any
  // other id lights up that organisation.
  //
  // Own level no longer carries an org in the URL (STIC-15615), so `levelOrgId` is
  // null there and the pill would fall through to "All". Supply the own org id
  // explicitly instead — the signal the pill was built around.
  //
  // At All it must read 0 even when the url names an org: a detail opened from
  // All carries `?org=196&scope=all`, and highlighting 196 would tell the user
  // they had been moved into that organisation when they had not. `levelOrgId`
  // is already null there, so this falls through to 0 on its own.
  const orgPillValue =
    level === MenuLevelConst.OWN ? loginInfo.organisation?.id ?? 0 : levelOrgId ?? 0;

  const handleHeaderImpersonate = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (!levelOrgId || isTargetSystem) return;
    let impersonateMode = IMPERSONATE_TYPE.VIEWER;
    if (!hasPermission(UserPermission.ORG_SUPPORT_LOGIN_VIEW_ONLY)) {
      impersonateMode = IMPERSONATE_TYPE.ADMIN;
    }
    onImpersonateUser({
      id: levelOrgId,
      type: impersonateMode,
      onBadRequest: () => {
        openErrorDialog(translate(LanguageKey.impersonation_insufficient_rights_key));
      },
    });
  };

  const findRouteInAllPaths = (allPaths: Record<string, MenuRoute>, path: string): MenuRoute | null => {
    // First try exact match
    if (allPaths[path]) {
      return allPaths[path];
    }
    // Then try prefix match for paths with dynamic segments (e.g., /site_map_view/514)
    for (const key in allPaths) {
      if (path.startsWith(key + "/")) {
        return allPaths[key];
      }
    }
    return null;
  };

  const getMenuByLocationPath = (path: string): MenuData => {
    const orgType = loginInfo?.organisation?.organisationType;
    const ownRoute = findRouteInAllPaths(ownLevelMenu.allPaths, path);
    const crossRoute = findRouteInAllPaths(crossLevelMenu.allPaths, path);

    // No `?org=` at all means own level, and that wins over the per-login order
    // below. Both menus register the same paths for the org-data pages — the own
    // level shows the login's own org, the cross level any org it can see — so
    // the path alone can't decide. Gated on the own-level menu actually having
    // the path, so a bare URL for a cross-only page falls through to the normal
    // order rather than stranding the user on a menu that can't render it.
    if (level === MenuLevelConst.OWN && ownRoute) {
      return ownLevelMenu;
    }

    // Drill-in from an own-level list page. Still needed after STIC-15615: 221
    // link sites build their URL by hand as `org=${urlOrgId}`, and `urlOrgId` is null at
    // own level, so they arrive carrying the junk value `org=null` — which reads
    // as cross level. Without this the sidebar would flip halfway through a task.
    //
    // Staying put is safe because the check is narrow: we only hold the own level
    // for a path the own-level menu declares as a *subPage* of one of its items,
    // i.e. a genuine child of the page we came from. Deliberate exits are
    // unaffected — the org pill pushes a top-level path.
    //
    // History-based, so a hard refresh on such a URL resolves by the normal
    // order instead. A link built with `withOrg` has no such problem: it appends
    // nothing at own level, so the level survives the refresh too.
    //
    // Only for junk or absent org values (`urlOrgId === null`). A link that carries
    // a REAL org id — the Organizations list's edit pencil, `?org=<id>` — means
    // the user is now browsing that organisation, and the cross level must win
    // even though the page is declared as an own-level subPage.
    if (
      selectedMenu === ownLevelMenu &&
      urlOrgId === null &&
      ownRoute?.subPages?.includes(path)
    ) {
      return ownLevelMenu;
    }

    // Past this point the URL carries no own-level marker, so a path both menus
    // claim belongs to the cross level. Only own-*exclusive* paths — the System
    // and partner artifacts that have no cross-org counterpart — may resolve to
    // the own menu here.
    const ownOnly = !!ownRoute && !crossRoute;

    // SYSTEM logins have no home menu, so cross-level is checked first — the
    // dashboards and reports that exist in both must resolve to the admin side.
    if (orgType === "SYSTEM") {
      if (crossRoute) {
        return crossLevelMenu;
      }
      if (ownRoute) {
        return ownLevelMenu;
      }
      // No homeMenu for system users — fall back to crossLevelMenu
      return crossLevelMenu;
    }

    // The url says cross level (`scope=all` or a foreign `?org=`), so a path the
    // cross-level menu serves belongs there. Without this a partner opening a
    // report schedule from the Explorer landed in the Home menu, because the
    // detail path /create_report was a subpage of the Report list in all three
    // menus and Home is checked first for shared paths (STIC-15790). The Home
    // menu has its own /create_report_home since STIC-15980.
    if (level === MenuLevelConst.CROSS && crossRoute) {
      return crossLevelMenu;
    }

    // A partner without the legacy Home menu has no Home to resolve to
    // (STIC-15365): its Home paths fall through to the own and cross levels
    // below, so the shared paths land on the admin side and the Home-only paths
    // reach rule 7, which hands them to the own level. The three dashboard paths (/suez_dashboard,
    // /bi_dashboard/, /all_dashboards) became HOME/OWN overlaps when the
    // Dashboards group moved to the own level (STIC-16186 R1), so for these
    // logins they now resolve one rule further down.
    const routeItem = homeMenuReachable ? findRouteInAllPaths(homeMenu.allPaths, path) : null;
    if (routeItem) {
      return homeMenu;
    }

    // PARTNER logins reach both levels, so the own menu must not swallow a
    // shared path just by being checked first — that made the cross level
    // unreachable for every org-data page once the two menus started sharing
    // paths (STIC-15365). NORMAL logins have no cross level to fall through to,
    // so for them `ownRoute` still decides.
    if (ownRoute && (ownOnly || orgType !== "PARTNER")) {
      return ownLevelMenu;
    }

    // PARTNER logins browse their customer organisations through the cross-level
    // menu as well. Checked after the home menu, the slot usePartnerMenu used to
    // occupy, so shared paths (reports, dashboards) still resolve to home first.
    if (orgType === "PARTNER" && crossRoute) {
      return crossLevelMenu;
    }

    // Terminal fallback for a path no menu registers. Own level rather than
    // cross for a partner without Home: it is where such a partner lands after
    // login and where its Admin button starts, so the sidebar stays on the menu
    // the user knows (STIC-15365).
    return homeMenuReachable ? homeMenu : ownLevelMenu;
  };

  useEffect(() => {
    if (
      homeMenu.loading ||
      ownLevelMenu.loading ||
      crossLevelMenu.loading
    ) {
      return;
    }

    // Now that every menu has resolved for the current URL org, record whether this
    // evaluation follows an org-context change (the loading guard above means we only
    // get here once the new org's menu is ready, so this is stable across the async
    // menu refetch that a switch triggers).
    const orgChanged = prevOrgIdRef.current !== levelOrgId;
    prevOrgIdRef.current = levelOrgId;

    const menu = getMenuByLocationPath(location.pathname);
    setSelectedMenu(menu);
    dispatch(actions.setMenuType(menu.type));

    // The own-level menu used to need `?org=<ownId>` in the URL before its
    // org-data items would read as visible, so a deep link arriving without one
    // had to be defaulted. Own level is now the absence of the param, so such a
    // link already *is* own level and the items are visible — nothing to do
    // (STIC-15615).
    const ownOrgId = loginInfo.organisation?.id;

    // Same deal for the cross-level menu, which now owns the org-scoped pages
    // too: an org-scoped deep link (/trackers, or /quotes_storefront_partner,
    // without ?org=) has to slice an org before its menu entry can be visible.
    // `orgScopedPaths` says "this page belongs to an org".
    //
    // The second guard tests *visibility*, not mere presence. `allPaths`
    // deliberately registers hidden entries so deep links still resolve to a
    // menu, and the two split concepts now contribute both of their level
    // entries to it — so "is it in allPaths" can no longer tell whether the page
    // is actually on offer at this level. `availableMenuPaths` can: it holds only
    // what the current level really shows.
    //
    // This keeps protecting Rule #5 items — no cross-org page, shown greyed —
    // because greyed entries are still `view: true` and so still available.
    // Auto-slicing an org for those would defeat their explainer page.
    //
    // Gated on being at cross level: a URL with no `?org=` at all is own level
    // now, and slicing an org there would push the user off the level they asked
    // for (STIC-15615).
    if (
      level === MenuLevelConst.CROSS &&
      !urlOrgId &&
      ownOrgId &&
      !crossLevelMenu.availableMenuPaths[location.pathname] &&
      crossLevelMenu.orgScopedPaths?.[location.pathname]
    ) {
      updateOrg(ownOrgId);
      return;
    }

    // The user was auto-landed on the (org/user) configured landing page but has no
    // permission to access it. Send them to their first available menu instead of a
    // dead-end /permission or /no_access screen (STIC-15267). This runs before the
    // empty-menu and protected-route checks so it covers a configured landing page in
    // any menu (e.g. ASSET_LIST in the home menu, or TRACKER_ADMIN in the admin menu).
    if (
      location.pathname !== "/" &&
      !location.pathname.includes("/bi_dashboard/") &&
      location.pathname === getHashFromLandingPage(loginInfo)
    ) {
      const landingRoute = menu.allPaths[location.pathname];
      const landingAccessible = !!(
        menu.availableMenuPaths[landingRoute?.to ?? ""] ||
        menu.availableMenuPaths[location.pathname]
      );
      if (!landingAccessible) {
        // Fall back to the menu this login lands in — Home when it has one, the
        // admin side otherwise (see landingMenu).
        const fallback =
          typeof landingMenu.defaultPath === "string"
            ? { pathname: landingMenu.defaultPath, search: "" }
            : landingMenu.defaultPath;
        // Guard against a self-loop when the fallback resolves to the same page.
        if (fallback.pathname && fallback.pathname !== location.pathname) {
          history.push(fallback, MENU_ROOT_STATE);
          return;
        }
      }
    }

    // empty menu
    if (
      Object.keys(menu.availableMenuPaths).length === 0 &&
      location.pathname !== "/permission"
    ) {
      history.push("/permission", MENU_ROOT_STATE);
      return;
    }
    // protected route
    const mainRoute = menu.allPaths[location.pathname];
    if (!mainRoute && location.pathname !== "/") return;
    if (
      location.pathname !== "/no_access" &&
      location.pathname !== "/permission" &&
      location.pathname !== "/"
    ) {
      if (
        (location.pathname.includes("/bi_dashboard/") &&
          !menu.availableMenuPaths["/all_dashboards"]) ||
        (!location.pathname.includes("/bi_dashboard/") &&
          (!mainRoute ||
            !(
              menu.availableMenuPaths[mainRoute.to ?? ""] ||
              menu.availableMenuPaths[location.pathname]
            )))
      ) {
        if (menu.forceMenuPaths && menu.forceMenuPaths[mainRoute.to ?? ""]) {
          const path = menu.defaultPath;
          history.push(withOrg(typeof path === "string" ? path : path.pathname), MENU_ROOT_STATE);
        } else if (orgChanged && levelOrgId) {
          // The page isn't available in the org just switched to (e.g. it lacks the
          // feature/permission that exposed it in the previous org). Land on the org
          // default page instead of a dead-end no_access screen (STIC-15494). A fresh
          // deep-link to an inaccessible page still falls through to no_access below.
          const path = menu.defaultPath;
          const landingPath = typeof path === "string" ? path : path.pathname;
          if (landingPath && landingPath !== location.pathname) {
            history.push(withOrg(landingPath), MENU_ROOT_STATE);
          } else {
            history.push("/no_access", MENU_ROOT_STATE);
          }
        } else {
          history.push("/no_access", MENU_ROOT_STATE);
        }
        return;
      }
    }

    // Remember where the user is on each level, for the org pill's fallback
    // (STIC-16144). Only from here on: everything above either redirects or
    // bounces, so a page that never rendered is never remembered.
    //
    // Gated on the level AND the menu that won, because the two can disagree:
    // an own-exclusive path whose entry is not gated on `atOwnLevel` (the
    // System landing dashboards) resolves to the own menu while the url is
    // still at cross level, and its availability map is then a cross-level one.
    //
    // Through `findRouteInAllPaths`, so a path with a dynamic segment
    // (/site_map_view/514) is remembered as its list page rather than as itself.
    const currentListPath =
      findRouteInAllPaths(menu.allPaths, location.pathname)?.to ?? location.pathname;
    if (level === MenuLevelConst.OWN && menu.menuSource === MenuSourceConst.OWN) {
      ownLevelAvailableRef.current = menu.availableMenuPaths;
      setLastOwnPage(currentListPath);
    } else if (level === MenuLevelConst.CROSS && menu.menuSource === MenuSourceConst.CROSS) {
      setLastCrossPage(currentListPath);
    }

    if (menu.type === MenuTypeConst.ADMIN) {
      // Top-level admin routes without ?org= are defaulted to the user's own
      // org earlier in this effect (and re-run), so reaching here means the
      // org is present (or this is a subpage, which keeps the URL's org).
      setLastAdminMenu(location);
    } else if (menu.type === MenuTypeConst.MAIN) {
      setLastHomeMenu(location);
    } else {
      setLastAdminMenu(location);
    }

    if (location.pathname === "" || location.pathname === "/") {
      let landingPath = {
        pathname: getHashFromLandingPage(loginInfo),
        search: "",
      };
      if (!landingMenu.availableMenuPaths[landingPath.pathname]) {
        landingPath = typeof landingMenu.defaultPath === "string"
          ? { pathname: landingMenu.defaultPath, search: "" }
          : landingMenu.defaultPath;
      }
      history.push(landingPath, MENU_ROOT_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, ownLevelMenu, crossLevelMenu, homeMenu]);

  useEffect(() => {
    if (ownLevelMenu.isAdminMenuAvailable) {
      setShowFooter(true);
      return;
    }

    const isShowFooter =
      (hasPermission(UserPermission.SYSTEM_ADMIN_VIEW) || isPartnerLogin) &&
      !!crossLevelMenu.isAdminMenuAvailable;
    setShowFooter(isShowFooter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownLevelMenu, crossLevelMenu]);

  /**
   * Org selector changed — including clearing it back to "All organisations".
   *
   * There is no menu switch here any more: cross-level owns organisation data at
   * BOTH levels, so picking an org and clearing it land in the same menu. The
   * only thing that differs is the level of the page and the `?org=` slice.
   */
  const onOrganisationChanged = (data: OrgSelectorData): void => {
    const levelMenu = crossLevelMenu;
    const wantOrgLevel = !!data.value;

    // Resolve an entity *detail* subpage (e.g. /users_update) to its parent list
    // page: the open record belongs to the org we're leaving, so carrying it over
    // would point at the wrong entity (STIC-15492).
    const currentParent =
      levelMenu.allPaths[location.pathname]?.to || location.pathname;
    const isDetailSubpage = currentParent !== location.pathname;

    /**
     * Keep the page's own query params when we stayed on it; drop the
     * detail-only ones (id/ownerOrg/type) when we fell back to a list. Both of
     * the level parameters go either way — the caller writes the one its level
     * needs, and own level is the absence of both.
     */
    const searchFor = (keptPage: boolean): URLSearchParams => {
      const params = new URLSearchParams(
        keptPage && !isDetailSubpage ? location.search : ""
      );
      params.delete("org");
      params.delete(SCOPE_PARAM);
      return params;
    };

    const pushLevel = (pathname: string, params: URLSearchParams): void => {
      const search = params.toString();
      history.push({ pathname, search: search ? `?${search}` : "" }, MENU_ROOT_STATE);
    };

    // The "System"/"Partner" tab is the one selection that isn't about browsing
    // org data: it asks for the login's own artifacts. Nothing in the
    // cross-level menu can represent that, so this branch lands in the
    // own-level menu.
    //
    // It used to go straight to that menu's default item, which threw away the
    // page the user was on even when the own level serves it — leaving "Users"
    // for "Organizations" on the way back from a round trip that had carried
    // "Users" across in the first place (STIC-16144). It now follows the same
    // three-step rule as the cross branch below.
    if (data.scope === "own") {
      // `.org` is the sliced-org side of a split concept, which is what own
      // level is: exactly one organisation, the login's own.
      const translated = levelMenu.levelIndex?.[currentParent]?.org ?? currentParent;
      // Not `ownLevelMenu.availableMenuPaths` — that map is computed for the
      // level the url is on now, and every own-level entry is gated on
      // `atOwnLevel`. See ownLevelAvailableRef.
      const ownAvailable = ownLevelAvailableRef.current;
      const ownDefault =
        typeof ownLevelMenu.defaultPath === "string"
          ? ownLevelMenu.defaultPath
          : ownLevelMenu.defaultPath.pathname;
      const servedHere = !!ownAvailable[translated];
      const pathname = servedHere
        ? translated
        : lastOwnPage && ownAvailable[lastOwnPage]
          ? lastOwnPage
          : ownDefault;
      // Own level is the absence of both parameters (STIC-15615) — `searchFor`
      // has already removed them, and nothing is written back.
      pushLevel(pathname, searchFor(servedHere));
      return;
    }

    // Translate the page to the level we're moving to. Entries whose two levels
    // collapsed to a single path map to themselves, so this is a no-op for them.
    const pair = levelMenu.levelIndex?.[currentParent];
    const translated = pair ? (wantOrgLevel ? pair.org : pair.cross) : currentParent;

    // Own-level pages (the System pill's Partners/Finance/Salesforce/... screens)
    // have no cross-level counterpart, so there is nothing to carry across.
    // Judged by the VISIBLE entries, not the registry of declared paths: a twin
    // that is declared but hidden for this login (a partner's cross-level
    // Tracker usage profile, row 28) used to count as served, and the route
    // guard then bounced the page to /no_access with no organisation to fall
    // back on (STIC-15790).
    const servedHere = !!levelMenu.availableMenuPaths[translated];
    const menuDefault =
      typeof levelMenu.defaultPath === "string"
        ? levelMenu.defaultPath
        : levelMenu.defaultPath.pathname;
    // Nothing to carry across, so return to where the user last was on this
    // level before falling back to its default item (STIC-16144) — landing on
    // "Asset trackers" out of nowhere is what made this switch feel arbitrary.
    // Re-checked against what the level shows *now*, so a remembered page the
    // organisation being selected does not offer cannot strand the user.
    const fallback =
      lastCrossPage && levelMenu.availableMenuPaths[lastCrossPage]
        ? lastCrossPage
        : menuDefault;
    const pathname = servedHere ? translated : fallback;

    // Two parameters, one job each: `org` names the organisation, `scope=all`
    // names the level. One of them is always written — dropping both would land
    // on own level, which is not what the selector was asked for.
    const params = searchFor(servedHere);
    if (data.value) {
      params.set("org", String(data.value));
    } else {
      params.set(SCOPE_PARAM, ORG_ALL);
    }

    pushLevel(pathname, params);
  };

  const onAdminButtonClicked = (): void => {
    // Back to the last admin page, else the own level on its first page —
    // Organizations on the "Partner" tab for a partner, Asset trackers for a normal
    // organisation (STIC-15365). Only logins with a Home menu see this button, so
    // SYSTEM never gets here.
    history.push(lastAdminMenu ?? ownLevelMenu.defaultPath);
  };

  const onHomeMenuClicked = (): void => {
    const path = lastHomeMenu || (typeof homeMenu.defaultPath === "string"
      ? { pathname: homeMenu.defaultPath, search: "" }
      : homeMenu.defaultPath);
    setSelectedMenu(homeMenu);
    history.push(path);
  };

  // The lower-left Home/Admin toggle only makes sense with a Home to toggle to.
  const isHomeButtonVisible = homeMenuReachable;

  const showNavIconBar = showFooter && isHomeButtonVisible;

  // The portal targets live in <Header>. Resolving them during render races with
  // mount order — if Header commits after this component, getElementById returns
  // null and the portal is silently skipped with no re-render to retry. Resolve
  // after commit instead, bailing out when unchanged so this can't loop.
  // Intentionally dependency-less: the targets can appear in any later commit,
  // so this has to re-check after every render. The updater returns `prev`
  // unchanged when both nodes are identical, so React bails out and no update
  // chain forms — which is what the lint rule is warning about.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const org = document.getElementById("header-org-selector");
    const support = document.getElementById("header-support-login");
    setHeaderSlots((prev) =>
      prev.org === org && prev.support === support ? prev : { org, support }
    );
  });

  return (
    <>
      {hasOrgSelector &&
        !isMobile &&
        headerSlots.org &&
        createPortal(
          <SntOrgMenuSelectorContainer
            onChange={onOrganisationChanged}
            value={orgPillValue}
            variant="pill"
            hideImpersonate
            // The header row is 56px tall: keep the tabs and the pill on one
            // line and let the org name truncate instead of wrapping under them.
            singleLine
          />,
          headerSlots.org
        )}
      {hasOrgSelector && isMobile && (
        <div className="p-2">
          <SntOrgMenuSelectorContainer
            onChange={onOrganisationChanged}
            value={orgPillValue}
            variant="pill"
            hideImpersonate
          />
          {canImpersonate && !isTargetSystem && (
            <SntButton
              onClick={handleHeaderImpersonate}
              className="mt-2"
              title={translate(LanguageKey.support_login_key)}
              style={{ height: 36, padding: "0 12px", fontSize: 14 }}
            >
              <span className="d-inline-flex align-items-center gap-2">
                <SntSupportUserIcon size={18} color="#e67e22" />
                <span>{translate(LanguageKey.support_login_key)}</span>
              </span>
            </SntButton>
          )}
        </div>
      )}
      {canImpersonate &&
        !isTargetSystem &&
        headerSlots.support &&
        createPortal(
          <HeaderIconButton
            title={
              isTargetSystem
                ? translate(LanguageKey.support_login_on_system_account_is_not_permitted_key)
                : translate(LanguageKey.support_login_key)
            }
            onClick={handleHeaderImpersonate}
          >
            <SntSupportUserIcon size={20} color="#e67e22" style={{ opacity: isTargetSystem ? 0.4 : 1 }} />
          </HeaderIconButton>,
          headerSlots.support
        )}
      {isAdminMenu && (
        <SidebarMenuSearch
          value={menuQuery}
          onChange={setMenuQuery}
          onClear={() => setMenuQuery("")}
          onEnter={() => {
            // Enter opens the first visible match through the very link its row
            // carries, so it lands where a click on that row would.
            const first = firstMenuItem(searchedRoutes);
            if (!first) return;
            history.push(
              buildMenuItemHref(first, {
                menuSource: selectedMenu.menuSource,
                orgId: levelOrgId !== null ? String(levelOrgId) : undefined,
                menuType: selectedMenu.type,
              }),
              MENU_ROOT_STATE
            );
          }}
        />
      )}
      <div className="sidebar-middle">
        {noMenuMatches && (
          <div className="sidebar-menu-search-empty">
            {translate(LanguageKey.v41_no_matching_search_results_key)}
          </div>
        )}
        <NavList
          // The LEVEL's org: at All a menu link must read `?org=all`, even while
          // the url names the org of the detail the user has open.
          orgId={levelOrgId !== null ? String(levelOrgId) : undefined}
          location={location}
          routes={searchedRoutes}
          collapsible={selectedMenu.type === homeMenu.type}
          menuType={selectedMenu.type}
          menuSource={selectedMenu.menuSource}
        />
      </div>
      {showNavIconBar || !whiteLabel ? (
        <div
          className={
            "sidebar-footer " +
            (selectedMenu.type === MenuTypeConst.MAIN ? "home-menu" : "admin-menu")
          }
        >
          {showNavIconBar && (
            <div className="nav-icon-bar">
              <div className="nav-icon-wrapper">
                <div
                  className={
                    selectedMenu.type === homeMenu.type
                      ? "nav-icon active"
                      : "nav-icon"
                  }
                  onClick={onHomeMenuClicked}
                >
                  <img alt="home" src={HomeIcon} />
                  <div>{translate(LanguageKey.home_key)}</div>
                </div>
              </div>
              <div className="nav-icon-wrapper">
                <div
                  className={
                    selectedMenu.type !== homeMenu.type
                      ? "nav-icon active"
                      : "nav-icon"
                  }
                  onClick={onAdminButtonClicked}
                >
                  <img alt="admin" src={CogIcon} />
                  <div>{translate(LanguageKey.sidebar_admin_key)}</div>
                </div>
              </div>
            </div>
          )}
          {!whiteLabel && (
            <div className="powered-by">
              <div>{translate(LanguageKey.powered_by_key)}</div>
              <img src={SensolusLogoIcon} alt="sensolus" />
            </div>
          )}
        </div>
      ) : (
        ""
      )}
      <ErrorDialog />
      <ImpersonateReasonDialogRoot />
    </>
  );
};

export default SidebarBody;
